import React, { useState, useEffect, useMemo, useLayoutEffect, useCallback } from "react";
import { SingleDatePicker } from "react-dates";
import moment from "moment";
import { momentToDate, toMoment, formatDate } from "@travellocal/utils/dateHelpers";
import { Dialog, DialogProps } from "../../layout";
import { Icomoon, Icon } from "../../brand";
import { PrimaryButton, SecondaryButton } from "../../forms";
import { useWindowDimensions } from "../../utils";
import "./EnquirySnoozeDialog.scss";

interface Props extends Pick<DialogProps, "isOpen" | "onClose"> {
  /**
   * Callback for when a date is chosen and submitted.
   */
  onDateSelect: (date: IDate) => void;

  /**
   * Optional callback for when the back CTA is clicked. If not defined `onClose` will be called.
   */
  onBackClick?: () => void;

  /**
   * The date that should be selected when the dialog is opened.
   */
  initialDate?: IDate;
}

/**
 * Component to prompt the LTO for a snooze date. See #3178
 * 
 * N.B. `import "react-dates/initialize";` must be somewhere in the application for the date picker component to work. See https://github.com/airbnb/react-with-styles/issues/137#issuecomment-391530082
 * 
 * @status review
 * - `react-dates` is quite a hefty dependency when we use just one component
 * - the performance of the date picker component is not good on mobile
 */
export const EnquirySnoozeDialog: React.FC<Props> = (props) => {

  const [selectedDate, setSelectedDate] = useState(props.initialDate);
  const [monthCount, setMonthCount] = useState(1);
  const windowDim = useWindowDimensions();
  const [containerRef, setContainerRef] = useState<HTMLElement | null>();

  useEffect(() => {
    setSelectedDate(props.initialDate)
  }, [props.initialDate]);

  const measureContainer = useCallback(node => {
    setContainerRef(node);
  }, []);

  // Show two months if there's enough horizontal space
  useLayoutEffect(() => {
    if (containerRef && windowDim.width) {
      const containerWidth = containerRef.getBoundingClientRect().width;
      const width = windowDim.width < containerWidth ? windowDim.width : containerWidth;
      setMonthCount(width >= 967 ? 2 : 1); // 984 = 1020 (max width) - padding + margins
    }
  }, [containerRef, windowDim.width]);

  const momentDate = useMemo(() => selectedDate ? toMoment(selectedDate) : undefined, [selectedDate]);

  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={props.onClose}
      ariaLabel="Dialog to choose a snooze date for this enquiry"
      className="enquiry-snooze-dialog"
      hasCloseButton
    >
      <div className="columns" ref={measureContainer}>
        <div className="column is-5 is-4-desktop enquiry-snooze-dialog__info is-flex">
          <Icon className="enquiry-snooze-dialog__icon" icomoon={Icomoon.alarm} />
          <h1 className="heading--2">Snooze this enquiry</h1>
          <p>
            If you don&apos;t need to do anything with this enquiry now, you can hide it until a certain date.
          </p>

          <SecondaryButton
            className="is-paddingless enquiry-snooze-dialog__cancel"
            onClick={props.onBackClick ?? props.onClose}
          >
            Back
          </SecondaryButton>
        </div>

        <div className="column" >
          <SingleDatePicker
            id="snooze_until_date"
            placeholder={"Choose a date..."}
            displayFormat="Do MMM YYYY"
            isOutsideRange={(day: moment.Moment) => {
              const tomorrow = moment.utc().startOf("day").add(1, "day");
              return day.isBefore(tomorrow);
            }}
            date={momentDate ?? null}
            onDateChange={newDate => {
              if (newDate != null) {
                setSelectedDate(momentToDate(newDate));
              }
            }}
            numberOfMonths={monthCount}
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onFocusChange={() => { }}
            focused
            noBorder
            block
            hideKeyboardShortcutsPanel
          />

          <div className="columns">
            <div className="column">
              <p className="enquiry-snooze-dialog__note">
                If the customer replies before{" "}
                {selectedDate ? formatDate(selectedDate, "Do MMM YYYY") : "the selected date"}
                , the enquiry will be unsnoozed automatically.
              </p>
            </div>

            <div className="column is-narrow">
              <PrimaryButton
                type="submit"
                onClick={() => selectedDate && props.onDateSelect(selectedDate)}
                disabled={selectedDate == null}
              >
                Snooze
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  )
};
